import React, {
  useEffect,
  useRef,
  useState,
  Dispatch as ReactDispatch,
  SetStateAction,
} from 'react';
import { t, Trans } from '@lingui/macro';
import Modal from '@mos-cat/modal';
import { Dispatch } from 'redux';
import { connect } from 'dva';

import { Button, Heading } from '@mos-cat/typography';
import ConfirmationPopover from '@mos-cat/confirmation-popover';
import { Child } from '../Child/Child';
import { ChildNameChange } from '../Child/ChildNameChange';
import BirthCertificate from '../Child/BirthCertificate';
import ChildAddress from '../Child/ChildAddress';
import ChildBornInCurrentMarriage from '../Child/ChildBornInCurrentMarriage';
import SecondParentStatus from '../Child/SecondParentStatus';
import ChildBornInNotCurrentMarriage from '../Child/ChildBornInNotCurrentMarriage';
import SecondParentInfo from '../Child/SecondParentInfo';
import DeleteChildInfoPopover from '../ConfirmationPopovers/DeleteChildInfoPopover';
import { addChildModalOpen } from '../../../models/modals';
import ChildDocument from '../Child/ChildDocument';
import ChildConfirmingDocuments from '../Child/ChildConfirmingDocuments';
import Link from '../../shared/Link/Link';
import {
  findScrollableParent,
  scrollToTop,
} from '../../../helpers/findScrollableParent';
import ChildrenIndirectHomestayDocuments from '../Child/ChildIndirectHomestayDocuments';
import { ChildProps } from '../../../models/child';
import { getNotHiddenChildren } from '../../../selectors/children';

import './ChildModal.pcss';
import { i18n } from '../../../helpers/i18n';
import { childAddressFulfilled } from '../../../selectors/child';

interface ChildModalProps {
  addChildModalOpen: boolean;
  addressFulfilled: boolean;
  fullName: string;
  addedChildren: ChildProps[];
  id?: string;

  onChildDelete(id: string | undefined): void;

  onModalClose(): void;

  validateChild(id: string | undefined): void;
}

function scrollToButton(visiblePopover, buttonRef) {
  if (visiblePopover !== 'cancel' || buttonRef.current === null) return;
  const modalOverlay = findScrollableParent(buttonRef.current);
  if (modalOverlay) modalOverlay.scrollTop = 9999;
}

function useIsDirty(
  ...deps
): [boolean, ReactDispatch<SetStateAction<boolean>>] {
  const [isDirty, setDirtyState] = useState(false);
  useEffect(() => {
    setDirtyState(false);
  }, deps);
  return [isDirty, setDirtyState];
}

function ChildModal({
  addChildModalOpen,
  onModalClose,
  fullName,
  id,
  onChildDelete,
  validateChild,
  addedChildren,
  addressFulfilled,
}: ChildModalProps) {
  const [isDirty, setDirtyState] = useIsDirty(addChildModalOpen);
  const [visiblePopover, setConfirmationVisible] = useState<
    'cancel' | 'delete' | null
  >(null);
  const buttonRef = useRef(null);
  useEffect(() => scrollToButton(visiblePopover, buttonRef), [visiblePopover]);

  useEffect(() => {
    if (addChildModalOpen)
      setTimeout(() => scrollToTop('.ChildModal .Modal-body'), 500);
  }, [addChildModalOpen]);

  const handleConfirmCancel = () => {
    setConfirmationVisible(null);
    onModalClose();
  };

  const headerContent = fullName ? (
    <Heading level={3} className="ChildModal__Title ym-hide-content">
      {fullName}
    </Heading>
  ) : (
    <Heading level={3} className="ChildModal__Title">
      <Trans>Добавление ребёнка</Trans>
    </Heading>
  );

  const onCloseAttempt = (event, source) => {
    if (source === 'mask') return;
    if (isDirty) {
      setConfirmationVisible('cancel');
      return;
    }
    handleConfirmCancel();
  };

  const handleSaveOrAdd = (id) => {
    validateChild(id);
    setConfirmationVisible(null);
  };

  const addedChild = addedChildren.find(({ id: childId }) => childId === id);

  const closeAllPopovers = () => setConfirmationVisible(null);

  const childFromElk = id && !addedChild;

  const hidden = addedChild ? addedChild.hidden : true;

  return (
    <Modal
      headerContent={headerContent}
      visible={addChildModalOpen}
      onClose={onCloseAttempt}
      className="ChildModal"
    >
      <div onChange={() => setDirtyState(true)}>
        {childFromElk && (
          <div className="Content__sub">
            <p className="Content__subText">
              <Trans>Данные о детях заполняются из личного кабинета.</Trans>
            </p>
            <p className="Content__subText">
              <Trans>
                <Link
                  href={i18n._(t`https://my.mos.ru/my/#profile|my_children`)}
                  target="_blank"
                >
                  Отредактируйте профиль
                </Link>
                , если вы хотите изменить их.
              </Trans>
            </p>
          </div>
        )}

        <Child />
        <BirthCertificate />
        <ChildDocument />
        <ChildAddress />
        {addressFulfilled && (
          <>
            <ChildBornInCurrentMarriage />
            <ChildBornInNotCurrentMarriage />
            <SecondParentStatus />
            <SecondParentInfo />
            <ChildConfirmingDocuments />
            <ChildrenIndirectHomestayDocuments />
            <ChildNameChange />
          </>
        )}
        <div className="ChildModal__footer">
          <div className="ChildModal__buttons ChildModal__buttons_left">
            <Button
              className="ChildModal__addBtn"
              primary
              onClick={addressFulfilled ? () => handleSaveOrAdd(id) : () => {}}
              disabled={!addressFulfilled}
            >
              <Trans
                id={
                  id && addedChildren.some((child) => child.id === id)
                    ? t`Сохранить сведения`
                    : t`Добавить ребёнка`
                }
              />
            </Button>

            <ConfirmationPopover
              floating
              className={'ChildModal__Popover ChildModal__Popover_cancel'}
              isPopoverOpen={visiblePopover === 'cancel'}
              title={i18n._(t`Отмена изменений`)}
              description={i18n._(t`Все изменения будут потеряны`)}
              onConfirm={handleConfirmCancel}
              onCancel={closeAllPopovers}
              acceptText={i18n._(t`Отменить изменения`)}
              cancelText={i18n._(t`Не отменять`)}
              onPopoverClose={closeAllPopovers}
            >
              <button
                ref={buttonRef}
                className="ChildModal__cancelBtn mos-link"
                onClick={() => {
                  if (isDirty) {
                    setConfirmationVisible('cancel');
                    return;
                  }
                  handleConfirmCancel();
                }}
              >
                <Trans>Отменить</Trans>
              </button>
            </ConfirmationPopover>
          </div>
          {!hidden && (
            <div className="ChildModal__buttons ChildModal__buttons_right">
              <DeleteChildInfoPopover
                isPopoverOpen={visiblePopover === 'delete'}
                wrapperClassName="ChildModal__deleteChildInfoPopover_wrapper"
                onConfirm={() => {
                  setConfirmationVisible(null);
                  onChildDelete(id);
                }}
                onCancel={closeAllPopovers}
                onPopoverClose={closeAllPopovers}
              >
                <button
                  type="button"
                  className="mos-link ChildModal__deleteBtn"
                  onClick={() => setConfirmationVisible('delete')}
                >
                  <Trans>Удалить сведения о ребёнке</Trans>
                </button>
              </DeleteChildInfoPopover>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

const mapDispatchToProps = (dispatch: Dispatch) => ({
  onChildDelete(payload) {
    dispatch({
      type: 'children/delete',
      payload,
    });
    dispatch({
      type: 'notHiddenChildren/delete',
      payload,
    });
    dispatch(addChildModalOpen(false));
  },
  onModalClose() {
    dispatch(addChildModalOpen(false));
  },
  validateChild(payload) {
    dispatch({
      type: 'child/validate',
      payload,
    });
  },
});

export default connect(
  (state) => ({
    fullName: state.child.fullName,
    id: state.child.id,
    addedChildren: getNotHiddenChildren(state),
    addChildModalOpen: state.modals.addChildModalOpen,
    addressFulfilled: childAddressFulfilled(state),
  }),
  mapDispatchToProps
)(ChildModal);
